import { GetStaticProps } from 'next';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Heading, HeadingType } from '../../../../components';
import { Breadcrumb } from '../../../../components/Atoms/BreadCrumb';
import { If } from '../../../../components/Atoms/Conditionals';
import { ArticleList } from '../../../../components/Calculators/calculator-revamp/components/ArticlesList';
import { ICALCULATOR_LAYOUT } from '../../../../components/Calculators/calculator-revamp/components/CalculatorLayout';
import { Share } from '../../../../components/Calculators/calculator-revamp/components/Share';
import {
  OTHER_CALCULATORS,
  POPULAR_CALCULATORS,
} from '../../../../components/Calculators/calculator-revamp/constants';
import { CalculatorLayoutStyle } from '../../../../components/Calculators/calculator-revamp/styles';
import { renderCalculator } from '../../../../components/Calculators/calculator-revamp/utils';
import { TopCTA } from '../../../../components/cms/layout/components';
import { b2cData } from '../../../../components/cms/layout/components/header';
import {
  getActiveFAQsInFAQFormat,
  getBreadCrumbData,
  stripUnwantedStyles,
} from '../../../../components/cms/layout/util';
import {
  attachTrackers,
  removeTrackers,
} from '../../../../components/cms/utils/commonUtils';
import { FaqComponent, FaqVariants } from '../../../../components/Common/FAQS';
import { ClearInNavbar } from '../../../../components/Navbar/ClearNavbar';
import { AtcTaxCalculator } from '../../../../components/paytax-calculator/atc-tax-calculator';
import { PaytaxCalculatorProvider } from '../../../../components/paytax-calculator/context';
import { IncomeTaxCalculator } from '../../../../components/paytax-calculator/income-tax-calculator';
import {
  CALCULATOR_PERMALINK,
  calculatorContentMap,
  previewText,
} from '../../../../components/paytax-calculator/utils';
import { ORG_SCHEMA } from '../../../../constants/componentStaticData';
import {
  LANGUAGE_CODE,
  LANGUAGE_ENUMS,
  LANGUAGE_VALUE,
  domainCode,
} from '../../../../constants/staticPageConstants';
import { Meta } from '../../../../layout/Meta';
import {
  staticPageGenerate,
  staticPagesPath,
} from '../../../../utils/static-pages/staticPages';
import { handleTrack } from '../../../../utils/tracker';
import { FM_ACTIONS } from '../../../../utils/UtilFunctions';

const TaxCalculator = ({ postData }: ICALCULATOR_LAYOUT) => {
  const [financialYear, setFinancialYear] = useState('2025');
  const [financialYearCalc, setFinancialYearCalc] = useState('2025');
  const calc_content = useRef(null);
  const calc_dom = calc_content.current;
  const { data } = postData;
  const calcData = data?.calculatorData?.parameters;

  useEffect(() => {
    if (data.permalink === 'taxcalculator-v1') {
      handleTrack({
        categoryName: '',
        label: 'taxcalculator-v1',
        action: FM_ACTIONS.pageview,
      });
    } else {
      handleTrack({
        categoryName: '',
        label: data.permalink,
        action: FM_ACTIONS.pageview,
      });
    }
  }, []);

  const BREADCRUMB_DATA = getBreadCrumbData(
    data,
    LANGUAGE_VALUE[data.language as keyof typeof LANGUAGE_VALUE]
  );
  const pageUrl = `https://cleartax.in/paytax/${data.permalink}`;

  const captureEvent = (e: MouseEvent<Element, MouseEvent>) => {
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };
  const shareUrl = `${previewText} ${pageUrl}`;
  useEffect(() => {
    handleTrack({
      categoryName: '',
      label: data.permalink,
      action: FM_ACTIONS.pageview,
    });
    data.content = stripUnwantedStyles(data.content);
  }, []);

  useEffect(() => {
    const trackers = attachTrackers(
      ['.calc-content a'],
      ['incontent_cta'],
      calc_dom
    );

    return () => {
      removeTrackers(trackers);
    };
  }, [calc_dom]);

  return (
    <>
      <ClearInNavbar data={b2cData} />
      <Meta
        title={data.meta_title}
        description={data.description}
        keywords={data.keywords}
        canonical={pageUrl}
        isOgEnabled={true}
        img="https://assets.clear.in/cleartax/images/1753522640_cleartaxhomepage_.png"
      />
      <TopCTA CTA={data.cta || ''} onClickCB={captureEvent} />
      <div className="wg-container">
        <Breadcrumb dataSet={BREADCRUMB_DATA} className="pt-6 sm:pt-3" />
        <div className="flex justify-between items-center">
          <div>
            <Heading
              variant={HeadingType.h1}
              content={data.title}
              className="mb-3 mt-12 sm:mt-6 sm:mb-3 text-s-32 leading-tight sm:text-s-24 sm:leading-8"
            />
          </div>
          <div className="pt-9 sm:pt-0">
            <Share
              url={shareUrl}
              domain={domainCode.cleartaxIn}
              language={LANGUAGE_ENUMS.English}
            />
          </div>
        </div>
        <div>
          <div className="text-s-14 py-2 mb-6  w-full sm:py-0 self-center sm:my-1  sm:w-full flex">
            {calculatorContentMap[data.permalink] || ''}
            <If
              condition={
                data.permalink === 'taxcalculator' &&
                financialYearCalc === '2025'
              }
            >
              <div className="bg-[#FEF3E3] text-[#A06C23] w-28 font-medium text-s-14 flex justify-center rounded-sm ml-2 sm:text-s-10 sm:h-6 sm:items-center">
                Latest Budget
              </div>
            </If>
          </div>
        </div>
      </div>
      <div className="wg-container calc-content" ref={calc_content}>
        <PaytaxCalculatorProvider
          value={{
            financialYear,
            setFinancialYear,
            financialYearCalc,
            setFinancialYearCalc,
          }}
        >
          {(() => {
            if (calcData) {
              return renderCalculator(calcData);
            }

            switch (data.permalink) {
              case 'taxcalculator':
                return (
                  <IncomeTaxCalculator
                    showSummary={true}
                    showSlabsTable={false}
                  />
                );
              default:
                return <AtcTaxCalculator />;
            }
          })()}
        </PaytaxCalculatorProvider>
        <CalculatorLayoutStyle>
          <div className="flex justify-between gap-6 calculator sm:flex-col">
            <div className="w-8/12 sm:w-full sm:my-6 font-medium text-s-20 sm:text-sm-16 text-font-200">
              <div dangerouslySetInnerHTML={{ __html: data.content }} />
              <FaqComponent
                FAQS={getActiveFAQsInFAQFormat(data.faq)}
                isShowSchema={true}
                variant="partner"
                faqIconColor={'#314259'}
                htmlFromApi={true}
              />
            </div>
            <div className="w-4/12 mt-14 sm:w-full right-articles-section">
              <div className="flex flex-col gap-8">
                <ArticleList article={POPULAR_CALCULATORS} />

                <ArticleList article={OTHER_CALCULATORS} />
              </div>
            </div>
          </div>
        </CalculatorLayoutStyle>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ORG_SCHEMA) }}
      ></script>
    </>
  );
};

export const getStaticPaths = async () => {
  if (process.env.DEPLOYMENT_DOMAIN !== 'cleartax.in') {
    return {
      paths: [],
      fallback: false,
    };
  }
  if (process.env.ENABLE_CALCULATOR_PAGES === 'true' || process.env.PAGE_URL) {
    const staticPaths = await staticPagesPath(
      LANGUAGE_CODE.English,
      domainCode.cleartaxIn,
      'fs-cache-dbs/cmsDATA.db',
      FaqVariants.calculator
    );

    const updatedPath = staticPaths?.paths?.filter((item) =>
      CALCULATOR_PERMALINK.includes(item.params.static || '')
    );

    staticPaths.paths = updatedPath;
    return { ...staticPaths };
  }
  return {
    paths: [],
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const staticPages = await staticPageGenerate(
    params,
    LANGUAGE_CODE.English,
    'fs-cache-dbs/cmsDATA.db',
    FaqVariants.calculator
  );
  return { ...staticPages };
};

export default TaxCalculator;
